import { useSelector } from "react-redux";
import { Redirect, Route, RouteComponentProps, RouteProps, Switch } from "react-router";
import { RouteType, TabType } from "src/enums";
import { ConfigInit, UserSession } from "src/store";
import { AboutUs } from "./about.us";
import { AddToHomeScreen } from "./add.to.home.screen";
import { Archive } from "./archive";
import { ArchiveView } from "./archive.view";
import { ContactUs } from "./contact.us";
import { Download } from "./download";
import { IOS } from "./ios";
import { Programs } from "./programs";
import { Login } from "./login";
import { NotFound } from "./not.found";
import { PackageDetail } from "./package.detail";
import { Packages } from "./packages";
import { Payment } from "./payment";
import { PaymentResult } from "./payment.result";
import { Profile } from "./profile";
import { ProgramView } from "./program.view";
import { PackageGuide } from "./package.guide";
import { _ } from "src/services";
import { Tv } from "./tv";
import { Offline } from "./offline";
import { Qrcode } from "./qrcode";


export const RoutesList = [
    { path: ["/", "/program", "/programs"], component: Programs, routeType: RouteType.Authenticated, title: () => _("programPageTitle"), type: TabType.Live },
    { path: ["/program/:id/:slug?"], component: ProgramView, routeType: RouteType.Authenticated, type: TabType.Live, dontSetTitle: true, },
    { path: ["/tv"], component: Tv, routeType: RouteType.Authenticated, title: () => _("tvPageTitle"), type: TabType.Tv },
    { path: ["/tv/:slug"], component: Tv, routeType: RouteType.Authenticated, title: () => _("tvPageTitle"), dontSetTitle: true, type: TabType.Tv },
    { path: ["/archive"], component: Archive, routeType: RouteType.Authenticated, title: () => _("archivePageTitle"), type: TabType.Archive },
    { path: ["/archive/:id/:slug?"], component: ArchiveView, routeType: RouteType.Authenticated, type: TabType.Archive, dontSetTitle: true, },
    { path: ["/ios"], component: IOS, routeType: RouteType.Authenticated, title: () => _("addToHomePageTitle"), noLayout: true },
    { path: ["/add-to-home-screen"], component: AddToHomeScreen, routeType: RouteType.Authenticated, title: () => _("addToHomePageTitle"), noLayout: true },
    { path: ["/payment/:orderId"], component: Payment, routeType: RouteType.Authenticated, title: () => _("orderPageTitle") },
    { path: ["/payment/result/:orderId"], component: PaymentResult, routeType: RouteType.Authenticated, title: () => _("paymentResultPageTitle") },
    { path: ["/about-us"], component: AboutUs, routeType: RouteType.Authenticated, title: () => _("aboutUs") },
    { path: ["/package-guide"], component: PackageGuide, routeType: RouteType.Authenticated, title: () => _("packageGuide") },
    { path: ["/subscription"], component: Packages, title: () => _("packagesPageTitle"), enabledBy: "isPackagingSystemEnabled" },
    { path: ["/subscription/:id/:programId?"], component: PackageDetail, title: () => _("packagesPageTitle") },
    { path: ["/download"], component: Download, routeType: RouteType.Authenticated, title: () => _("antenAppsTitle") },
    { path: ["/contact-us"], component: ContactUs, routeType: RouteType.Authenticated, title: () => _("contactUs") },
    { path: ["/login"], component: Qrcode, routeType: RouteType.Anonymous },
    { path: ["/profile", "/profile/:route"], component: Profile, routeType: RouteType.Authenticated, title: () => _("myProfile") },
    { path: ["*"], component: NotFound, routeType: RouteType.Authenticated, title: () => _("notFoundPageTitle") },
];


export const Routes: React.FC = () => {

    const config: ConfigInit = useSelector((state: any) => state.ConfigState);

    let routes = [...RoutesList.map((x) => ({ ...x }))];

    if (config.loaded && config.config.appSetting.singleProgramId) {
        const landing = routes.findIndex((x) => x.path.includes("/"));

        if (landing >= 0) {

            routes = [
                ...routes.slice(0, landing),
                { ...routes[landing], component: () => <Redirect to={`/program/${config.config.appSetting.singleProgramId}`} /> },
                ...routes.slice(landing + 1)
            ];

        }
    }

    const filtered = routes
        .filter((x) => x.type === undefined || config.loading || config.config.appSetting.activeTabs.some(((y) => y.activeTabType === x.type && !y.origin)))
        //@ts-ignore
        .filter((x) => !x.enabledBy || config.loading || config.config.appSetting[x.enabledBy]);

    if (!config.loaded) {
        filtered.forEach((x) => x.component = Offline);
    }

    if (filtered.every((x) => x.path.indexOf("/") < 0)) {
        const tvRoute = filtered.find((x) => x.path.indexOf("/tv") > -1);
        const archiveRoute = filtered.find((x) => x.path.indexOf("/archive") > -1);
        const aboutRoute = filtered.find((x) => x.path.indexOf("/about-us") > -1);

        const homeRoute = tvRoute || archiveRoute || aboutRoute;

        if (homeRoute) {
            homeRoute.path = ["/", ...homeRoute.path];
        }
    }

    return (
        <Switch>
            {
                filtered.map((x, index) => {

                    if (x.routeType === RouteType.Authenticated)
                        return (
                            <AuthenticatedRoute exact key={index} path={x.path}>
                                <x.component />
                            </AuthenticatedRoute>
                        );


                    if (x.routeType === RouteType.Anonymous)
                        return (
                            <PublicRoute exact key={index} path={x.path}>
                                <x.component />
                            </PublicRoute>
                        );

                    return (<Route exact key={index} component={x.component} path={x.path} />);
                })
            }
        </Switch>
    );
};


const AuthenticatedRoute: React.FC<RouteProps> = ({ children, ...rest }) => {
    const user: UserSession = useSelector((state: any) => state.UserState);

    const render = (props: RouteComponentProps<any>) => {
        if (user.loggedIn) return (children);
        else return (<Redirect to={{ pathname: "/login", state: { from: props.location } }} />);
    };

    return (<Route {...rest} render={render as any} />);
};

const PublicRoute: React.FC<RouteProps> = ({ children, ...rest }) => {
    const user: UserSession = useSelector((state: any) => state.UserState);

    const render = (props: RouteComponentProps<any>) => {
        if (!user.loggedIn) return (children);
        else return (<Redirect to={{ pathname: "/", state: { from: props.location } }} />);
    };

    return (<Route {...rest} render={render as any} />);
};